// Port de SelectQuality : lecture d'un manifeste HLS et choix d'une variante.
//
// Les manifestes nous arrivent déjà réécrits par le proxy — chaque URI y est un
// chemin /p?u=…&r=… absolu. On n'a donc plus de résolution relative à faire,
// mais on garde `new URL(…, base)` par sécurité si un manifeste brut passait.

export interface Variant {
  height: number;
  url: string;
}

export interface Manifest {
  /** Variantes d'un master, de la plus haute à la plus basse. Vide quand le
   *  lecteur sert directement une playlist de segments. */
  variants: Variant[];
  /** Le manifeste est une playlist de segments jouable telle quelle. */
  isMedia: boolean;
}

const RESOLUTION_RE = /RESOLUTION=\d+x(\d+)/;

/** Analyse un manifeste. Retourne null s'il n'est ni un master exploitable ni
 *  une playlist de segments — le lecteur est alors considéré comme mort et
 *  l'appelant passe au suivant. */
export function parseManifest(body: string, base: string): Manifest | null {
  const variants: Variant[] = [];
  let lastHeight = 0;

  for (const raw of body.split("\n")) {
    const line = raw.trim();

    if (line.startsWith("#EXT-X-STREAM-INF")) {
      // une variante déclarée via URI= n'est pas un flux vidéo à sélectionner
      if (line.includes("URI=")) {
        lastHeight = 0;
        continue;
      }

      const match = RESOLUTION_RE.exec(line);
      lastHeight = match ? Number(match[1]) : 0;

      continue;
    }

    if (lastHeight > 0 && line !== "" && !line.startsWith("#")) {
      variants.push({ height: lastHeight, url: new URL(line, base).toString() });
      lastHeight = 0;
    }
  }

  if (variants.length === 0) {
    // certains lecteurs servent directement une playlist de segments, sans
    // niveau de qualité à choisir : elle est jouable telle quelle
    if (body.includes("#EXTINF")) return { variants: [], isMedia: true };

    return null;
  }

  variants.sort((a, b) => b.height - a.height);

  return { variants, isMedia: false };
}

/** Durée sous laquelle un dernier segment n'est qu'un reliquat d'empaquetage. */
const STUB_MAX_DURATION = 0.5;

/** Retire d'une playlist VOD son dernier segment quand il ne dure presque rien.
 *
 *  embed4me termine ses épisodes par un segment fMP4 de 0,064 s dont la piste
 *  vidéo, vide, porte un `tfdt` à 0 au lieu de la fin de l'épisode. hls.js s'en
 *  sert pour recaler toute la timeline : chargé après un seek vers la fin, il
 *  décale tout ce qui suit d'une durée d'épisode — 24 minutes affichées en 48,
 *  et plus rien de lisible dans la seconde moitié. Ce reliquat ne contient que
 *  quelques échantillons audio : le perdre ne s'entend pas. */
export function dropTrailingStub(body: string): string {
  if (!body.includes("#EXT-X-ENDLIST")) return body;

  const lines = body.split("\n");

  let uri = -1;

  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i]!.trim();

    if (line !== "" && !line.startsWith("#")) {
      uri = i;
      break;
    }
  }

  if (uri === -1) return body;

  let extinf = -1;

  for (let i = uri - 1; i >= 0; i--) {
    const line = lines[i]!.trim();

    if (line.startsWith("#EXTINF:")) {
      extinf = i;
      break;
    }

    if (line !== "" && !line.startsWith("#")) return body;
  }

  if (extinf === -1) return body;

  const duration = Number.parseFloat(lines[extinf]!.trim().slice("#EXTINF:".length));
  if (!(duration < STUB_MAX_DURATION)) return body;

  // un segment unique n'est pas un reliquat : c'est tout le flux
  const segments = lines.filter((line) => line.trim().startsWith("#EXTINF:")).length;
  if (segments < 2) return body;

  lines.splice(extinf, uri - extinf + 1);

  return lines.join("\n");
}

export type Quality = "best" | "worst" | "1080p" | "720p" | "480p";

export function pickVariant(variants: Variant[], quality: Quality): Variant | null {
  if (variants.length === 0) return null;

  const first = variants[0]!;
  const last = variants[variants.length - 1]!;

  switch (quality) {
    case "best":
      return first;
    case "worst":
      return last;
    case "1080p":
      return variants.find((v) => v.height >= 1080) ?? first;
    case "720p":
      return variants.find((v) => v.height >= 720 && v.height < 1080) ?? first;
    case "480p":
      return variants.find((v) => v.height >= 480 && v.height < 720) ?? first;
  }
}
